import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import FocusTrap from 'focus-trap-react';

export function Dialog({ open, onOpenChange, children }) {
  const containerRef = useRef();

  // ESC key to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    if (open) document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onOpenChange]);

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[12000] flex items-center justify-center">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm pointer-events-auto"
            onClick={() => onOpenChange(false)}
            onTouchStart={(e) => {
              // Don't close on touch if clicking inside dialog content
              if (e.target === e.currentTarget) {
                onOpenChange(false)
              }
            }}
            aria-hidden="true"
          />

          {/* Dialog Content */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="relative z-[12001] w-full max-w-lg mx-2 sm:mx-4 rounded-2xl overflow-hidden"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dialog-title"
            aria-describedby="dialog-description"
            ref={containerRef}
          >
            <FocusTrap>
              <div>{children}</div>
            </FocusTrap>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

export function DialogContent({ children, className = "" }) {
  return (
    <div
      className={`bg-app-surface-elevated rounded-2xl shadow-app-xl border border-app-border p-6 relative overflow-hidden ${className}`}
    >
      {children}
    </div>
  );
}

export function DialogHeader({ children, className = "" }) {
  return (
    <div className={`mb-4 flex flex-col space-y-1.5 text-center sm:text-left ${className}`}>{children}</div>
  );
}

export function DialogTitle({ children, className = "" }) {
  return (
    <h2
      id="dialog-title"
      className={`text-lg font-semibold leading-none tracking-tight text-gray-900 dark:text-gray-100 ${className}`}
    >
      {children}
    </h2>
  );
}

export function DialogDescription({ children, className = "" }) {
  return (
    <p
      id="dialog-description"
      className={`text-sm text-gray-600 dark:text-gray-400 ${className}`}
    >
      {children}
    </p>
  );
}

export function DialogFooter({ children, className = "" }) {
  return (
    <div
      className={`mt-6 flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 ${className}`}
    >
      {children}
    </div>
  );
}

export function DialogClose({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="absolute top-4 right-4 text-app-text-tertiary hover:text-app-text-secondary transition-colors duration-200 p-1.5 rounded-full hover:bg-app-surface-hover"
      aria-label="Luk dialog"
    >
      <X className="w-5 h-5" />
    </button>
  );
}